
import React, { useState, useEffect } from 'react';
import { buscarLeads, type ExternalLead } from './lib/leads-externos';
import { 
  Search, 
  Filter, 
  MessageSquare, 
  CheckCircle2, 
  Clock, 
  MapPin, 
  Globe, 
  Mail, 
  Phone, 
  ExternalLink, 
  ArrowRight,
  ChevronRight,
  X,
  Star,
  ShoppingBag,
  ShieldCheck,
  Smartphone,
  Calendar,
  Zap,
  Settings
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export default function ExternalLeads() {
  const [leads, setLeads] = useState<ExternalLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<ExternalLead | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      // A ordenação e o limite agora são do SERVIDOR, não uma gentileza do
      // cliente: o contrato não tem listagem sem teto.
      setLeads(await buscarLeads(50));
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = leads.filter(lead => 
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (filterCity === '' || lead.city === filterCity) &&
    (filterCategory === '' || lead.category === filterCategory)
  );

  const cities = Array.from(new Set(leads.map(l => l.city))).filter(Boolean);
  const categories = Array.from(new Set(leads.map(l => l.category))).filter(Boolean);

  const getWhatsAppStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'valid': return 'text-green-500 bg-green-50 border-green-200';
      case 'pending': return 'text-amber-500 bg-amber-50 border-amber-200';
      case 'invalid': return 'text-red-500 bg-red-50 border-red-200';
      default: return 'text-gray-400 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="p-8 font-sans text-slate-900 min-h-full">
      {/* Filtros */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input 
            type="text" 
            placeholder="Buscar por nome..."
            className="w-full rounded-sm border border-zinc-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-orange-700 focus:outline-none transition-colors"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="rounded-sm border border-zinc-200 bg-white px-4 py-2 text-sm focus:border-orange-700 focus:outline-none transition-colors"
          value={filterCity}
          onChange={(e) => setFilterCity(e.target.value)}
        >
          <option value="">Todas as Cidades</option>
          {cities.map(city => <option key={city} value={city}>{city}</option>)}
        </select>
        <select 
          className="rounded-sm border border-zinc-200 bg-white px-4 py-2 text-sm focus:border-orange-700 focus:outline-none transition-colors"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="">Todas as Categorias</option>
          {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        <div className="flex items-center justify-end text-[10px] font-bold uppercase tracking-widest text-slate-400">
          {filteredLeads.length} leads no radar
        </div>
      </div>

      {/* Tabela de Leads */}
      <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-100 text-[10px] font-bold uppercase tracking-widest text-slate-500 border-b border-zinc-200">
            <tr>
              <th className="px-6 py-4">Lead</th>
              <th className="px-6 py-4">Localização</th>
              <th className="px-6 py-4">Reputação</th>
              <th className="px-6 py-4">WhatsApp</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td colSpan={5} className="px-6 py-8 h-16 bg-zinc-50/50"></td>
                </tr>
              ))
            ) : filteredLeads.map((lead) => (
              <tr 
                key={lead.id} 
                className="group cursor-pointer transition-colors hover:bg-orange-50/30"
                onClick={() => setSelectedLead(lead)}
              >
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="font-bold text-slate-900">{lead.name}</span>
                    <span className="text-[10px] font-mono text-slate-400 uppercase">{lead.category}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1 text-slate-600">
                    <MapPin className="h-3 w-3" />
                    <span className="text-xs">{lead.city}, {lead.state}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-orange-500 text-orange-500" />
                    <span className="font-bold">{lead.rating}</span>
                    <span className="text-[10px] font-mono text-slate-400">({lead.reviews})</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 rounded-sm border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${getWhatsAppStatusColor(lead.whatsapp_status || lead.whatsappStatus)}`}>
                    <MessageSquare className="h-3 w-3" />
                    {lead.whatsapp_status || lead.whatsappStatus || 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <ChevronRight className="h-4 w-4 text-zinc-300 transition-transform group-hover:translate-x-1 group-hover:text-orange-700" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dossiê do Lead (Modal Centralizado) */}
      <AnimatePresence>
        {selectedLead && (
          <LeadDetailsModal 
            lead={selectedLead} 
            onClose={() => setSelectedLead(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}

interface LeadDetailsModalProps {
  lead: ExternalLead;
  onClose: () => void;
}

function LeadDetailsModal({ lead, onClose }: LeadDetailsModalProps) {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    { id: 0, label: 'Identidade', icon: <Smartphone className="h-4 w-4" /> },
    { id: 1, label: 'Contato', icon: <Phone className="h-4 w-4" /> },
    { id: 2, label: 'Localização', icon: <MapPin className="h-4 w-4" /> },
    { id: 3, label: 'Reputação', icon: <Star className="h-4 w-4" /> },
    { id: 4, label: 'Operação', icon: <Clock className="h-4 w-4" /> },
    { id: 5, label: 'WhatsApp', icon: <MessageSquare className="h-4 w-4" /> },
    { id: 6, label: 'Catálogo', icon: <ShoppingBag className="h-4 w-4" /> },
    { id: 7, label: 'Sistema', icon: <Settings className="h-4 w-4" /> },
  ];

  const whatsappData = lead.whatsapp_data || lead.whatsappData || {};

  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Identidade
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <DataField label="Nome da Empresa" value={lead.name} />
              <DataField label="Categoria / Nicho" value={lead.category} />
              <DataField label="Bio / Descrição" value={whatsappData.about || lead.category} />
              <div className="flex flex-wrap gap-2 mt-2">
                {(lead.category || '').split(',').map(tag => (
                  <span key={tag} className="px-3 py-1 bg-orange-50 text-orange-700 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-orange-200">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );
      case 1: // Contato
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <DataField label="Telefone" value={lead.phone} isMono />
              <DataField label="WhatsApp JID" value={whatsappData.jid || lead.phone} isMono />
              <DataField label="Website" value={whatsappData.website || 'N/A'} isLink />
              <DataField label="Booking URL" value={whatsappData.bookingUrl || 'N/A'} isLink />
            </div>
          </div>
        );
      case 2: // Localização
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <DataField label="Endereço Completo" value={lead.address} />
              <div className="grid grid-cols-2 gap-6">
                <DataField label="Cidade" value={lead.city} />
                <DataField label="Estado" value={lead.state} />
              </div>
              <div className="h-48 bg-zinc-50 rounded-sm border border-dashed border-zinc-200 flex items-center justify-center text-slate-400 gap-2">
                <MapPin className="h-5 w-5" />
                <span className="text-xs font-mono">Visualização de Mapa Indisponível</span>
              </div>
            </div>
          </div>
        );
      case 3: // Reputação
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-6 bg-orange-50 rounded-sm border border-orange-100 text-center">
                <p className="text-[10px] font-bold text-orange-600 uppercase mb-1">Rating</p>
                <p className="text-3xl font-black text-orange-700 font-mono">{lead.rating || '0.0'}</p>
              </div>
              <div className="p-6 bg-zinc-50 rounded-sm border border-zinc-100 text-center">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Reviews</p>
                <p className="text-3xl font-black text-slate-900 font-mono">{lead.reviews || 0}</p>
              </div>
              <div className="p-6 bg-zinc-50 rounded-sm border border-zinc-100 text-center">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Fotos</p>
                <p className="text-3xl font-black text-slate-900 font-mono">12+</p>
              </div>
            </div>
          </div>
        );
      case 4: // Operação
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <DataField label="Status" value="OPERACIONAL" />
              <DataField label="Nível de Preço" value="$$ (Médio)" />
            </div>
            <div className="bg-zinc-50 p-6 rounded-sm border border-zinc-100">
              <p className="text-[10px] font-bold text-slate-400 uppercase mb-4">Horários</p>
              <div className="space-y-2">
                {['Segunda a Sexta', 'Sábado', 'Domingo'].map(day => (
                  <div key={day} className="flex justify-between text-xs">
                    <span className="text-slate-500">{day}</span>
                    <span className="font-bold text-slate-900 font-mono">08:00 - 18:00</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 5: // WhatsApp
        return (
          <div className="space-y-6">
            <div className="p-6 bg-orange-50 rounded-sm border border-orange-100 flex items-center gap-4">
              <div className="h-12 w-12 bg-orange-700 rounded-sm flex items-center justify-center text-white shadow-lg shadow-orange-100">
                <MessageSquare className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-orange-700 uppercase">WhatsApp Business</p>
                <p className="text-sm font-mono text-orange-900">{whatsappData.jid || 'N/A'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <DataField label="Status da Conta" value={lead.whatsapp_status || 'VALID'} />
              <DataField label="Verificado" value={whatsappData.isVerified ? 'SIM' : 'NÃO'} />
            </div>
            <DataField label="Recado (About)" value={whatsappData.about || 'N/A'} />
          </div>
        );
      case 6: // Catálogo
        const catalog = lead.catalog || whatsappData.catalog || [];
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {catalog.length > 0 ? catalog.map((item: any, idx: number) => (
                <div key={idx} className="group border border-zinc-100 rounded-sm p-3 bg-white hover:border-orange-700 transition-all hover:shadow-md">
                  <div className="aspect-square bg-zinc-50 rounded-sm overflow-hidden mb-3">
                    <img src={item.imageUrl || `https://picsum.photos/seed/${idx}/200`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" referrerPolicy="no-referrer" />
                  </div>
                  <p className="text-xs font-bold text-slate-900 truncate uppercase tracking-tight">{item.title}</p>
                  <p className="text-[10px] text-orange-700 font-mono mt-1 font-bold">{item.currency || 'R$'} {item.price}</p>
                </div>
              )) : (
                <div className="col-span-2 py-20 text-center border-2 border-dashed border-zinc-100 rounded-sm text-slate-400 text-xs italic font-mono">
                  Nenhum produto no catálogo.
                </div>
              )}
            </div>
          </div>
        );
      case 7: // Sistema
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <DataField label="ID Interno" value={lead.id} isMono />
              <DataField label="Data de Mineração" value={new Date(lead.createdAt).toLocaleString()} isMono />
              <div className="p-6 bg-slate-900 rounded-sm text-slate-400 font-mono text-[10px] space-y-1">
                <p className="text-slate-500 mb-2 uppercase font-bold tracking-widest">Logs do Sistema</p>
                <p><span className="text-orange-500">[OK]</span> Lead validado via Google Maps</p>
                <p><span className="text-orange-500">[OK]</span> WhatsApp verificado com sucesso</p>
                <p><span className="text-zinc-500">[INFO]</span> Metadados de catálogo extraídos</p>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="absolute inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
      />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-4xl max-h-[90vh] bg-zinc-50 rounded-sm border border-zinc-300 shadow-2xl overflow-hidden flex flex-col animate-fade-in"
      >
        {/* Barra de Gradiente Industrial */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-900 via-orange-700 to-slate-900 z-20"></div>

        {/* Header */}
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between bg-white pt-7">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-slate-900 rounded-sm flex items-center justify-center text-white shadow-lg shadow-zinc-200">
              <Zap className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tighter uppercase">{lead.name}</h2>
              <div className="flex items-center gap-2 text-[10px] font-mono text-slate-400">
                <span className="text-orange-600 font-bold">● RADAR ATIVO</span>
                <span>|</span>
                <span>ID: {lead.id.substring(0, 12)}</span>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 rounded-sm transition-colors text-slate-400 hover:text-slate-900"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs Horizontais */}
        <div className="flex border-b border-zinc-200 bg-zinc-50/50 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 transition-all border-b-2 whitespace-nowrap ${
                activeTab === tab.id 
                ? 'text-slate-900 border-orange-700 bg-white font-bold' 
                : 'text-slate-400 border-transparent hover:text-slate-700 hover:bg-zinc-100/50'
              }`}
            >
              <span className={activeTab === tab.id ? 'text-orange-700' : 'text-slate-400'}>
                {tab.icon}
              </span>
              <span className="text-[10px] uppercase font-bold tracking-widest">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-8 bg-white">
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-200 bg-zinc-50/50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="px-4 py-2 bg-white rounded-sm border border-zinc-200 shadow-sm">
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Lead Score</p>
              <p className="text-lg font-black text-orange-700 font-mono">9.8/10</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="px-6 py-3 border border-zinc-200 rounded-sm text-slate-600 text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-all">
              Exportar
            </button>
            <button className="px-8 py-3 bg-slate-900 text-white rounded-sm text-[10px] font-bold uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl flex items-center gap-2">
              Importar para o CRM
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function DataField({ label, value, isMono = false, isLink = false }: { label: string, value: string, isMono?: boolean, isLink?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
      {isLink && value !== 'N/A' ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-sm font-bold text-orange-700 hover:underline flex items-center gap-1 font-mono">
          {value}
          <ExternalLink size={12} />
        </a>
      ) : (
        <span className={`text-sm font-bold text-slate-900 ${isMono ? 'font-mono' : ''}`}>
          {value || 'N/A'}
        </span>
      )}
    </div>
  );
}
